import { useEffect, useState } from "react";
import { clearAtlasExterior } from "./pet-atlas-matte";
import { t } from "./i18n";

const surfaces = new Map<string, Promise<string>>();

// The original Lv1 atlases have an opaque white exterior. Remove only white
// pixels connected to the sheet edge; enclosed white fur and eyes stay opaque.
export function atlasSurface(source: string): Promise<string> {
  const cached = surfaces.get(source);
  if (cached) return cached;
  const result = new Promise<string>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) throw new Error(t("無法準備夥伴畫面"));
        context.drawImage(image, 0, 0);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
        clearAtlasExterior(pixels.data, pixels.width, pixels.height);
        context.putImageData(pixels, 0, 0);
        canvas.toBlob((blob) => blob ? resolve(URL.createObjectURL(blob)) : reject(new Error(t("無法準備夥伴畫面"))), "image/webp", .94);
      } catch (error) { reject(error); }
    };
    image.onerror = () => reject(new Error(t("夥伴圖片載入失敗")));
    image.src = source;
  });
  surfaces.set(source, result);
  void result.catch(() => surfaces.delete(source));
  return result;
}

export function usePetAtlasSurface(source: string, enabled: boolean) {
  const [state, setState] = useState<{ source: string; url?: string; error?: boolean }>();
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void atlasSurface(source).then(
      (url) => { if (!cancelled) setState({ source, url }); },
      () => { if (!cancelled) setState({ source, error: true }); },
    );
    return () => { cancelled = true; };
  }, [source, enabled]);
  return { url: enabled ? state?.source === source ? state.url : undefined : source, error: enabled && state?.source === source && state.error };
}
